import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Calendar,
  BookOpen,
  CheckCircle2,
  Plus,
  Edit2,
  Save,
  Download,
  FileText,
  Copy,
  Trash2,
  Star,
  FolderOpen,
  Globe,
  BookMarked,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  Unlink,
  Quote,
  MessageSquare,
  Lightbulb,
  Link as LinkIcon,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import GoogleDocsPicker from '../components/GoogleDocsPicker';
import GoogleDocsPanel from '../components/GoogleDocsPanel';
import GmailPanel from '../components/GmailPanel';

// IB source count targets by project type
const IB_SOURCE_TARGETS = {
  EE:             { min: 10, target: 15 },
  IA:             { min: 5,  target: 8  },
  TOK_ESSAY:      { min: 3,  target: 5  },
  TOK_EXHIBITION: { min: 3,  target: 6  },
};

const sourceTypeIcons = {
  WEBSITE: Globe,
  BOOK:    BookMarked,
  JOURNAL: FileText,
  PDF:     FileText,
  OTHER:   BookOpen,
};

const noteTypeLabels = {
  GENERAL:    'General',
  SUMMARY:    'Summary',
  QUOTE:      'Quote',
  EVALUATION: 'Evaluation',
  RELEVANCE:  'Relevance',
};

const noteTypeColors = {
  GENERAL:    'bg-slate-100 text-slate-700',
  SUMMARY:    'bg-blue-100 text-blue-700',
  QUOTE:      'bg-purple-100 text-purple-700',
  EVALUATION: 'bg-amber-100 text-amber-700',
  RELEVANCE:  'bg-green-100 text-green-700',
};

const noteTypeIcons = {
  GENERAL:    MessageSquare,
  SUMMARY:    FileText,
  QUOTE:      Quote,
  EVALUATION: Lightbulb,
  RELEVANCE:  LinkIcon,
};

// Star rating component
const StarRating = ({ value, onChange, disabled }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n === value ? 0 : n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          className="p-0.5 rounded disabled:cursor-default"
          aria-label={`Rate ${n} star${n !== 1 ? 's' : ''}`}
        >
          <Star
            className={`w-4 h-4 transition-colors ${
              n <= (hovered || value || 0)
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const projectTypeColors = {
  EE: 'bg-rose-50 text-rose-700 border-rose-200',
  IA: 'bg-sky-50 text-sky-700 border-sky-200',
  TOK_ESSAY: 'bg-amber-50 text-amber-700 border-amber-200',
  TOK_EXHIBITION: 'bg-amber-50 text-amber-700 border-amber-200',
};

const statusOptions = [
  { value: 'PLANNING', label: 'Planning' },
  { value: 'RESEARCH', label: 'Research' },
  { value: 'DRAFTING', label: 'Drafting' },
  { value: 'REVIEWING', label: 'Reviewing' },
  { value: 'FINAL', label: 'Final' },
];

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Research tab state
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [addSourceInput, setAddSourceInput] = useState('');
  const [addSourceFetching, setAddSourceFetching] = useState(false);
  const [addSourceMeta, setAddSourceMeta] = useState(null);
  const [addSourceSaving, setAddSourceSaving] = useState(false);
  const [expandedSources, setExpandedSources] = useState({});
  const [findSourcesOpen, setFindSourcesOpen] = useState(false);
  const [browserSearch, setBrowserSearch] = useState('');
  const [browserResults, setBrowserResults] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      const [projectRes, docsRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/project-docs/${id}`)
      ]);
      setProject(projectRes.data.project);
      setEditData(projectRes.data.project);
      setDocs(docsRes.data.docs || []);
      
      // Select current doc by default
      const currentDoc = docsRes.data.docs?.find(d => d.isCurrent);
      if (currentDoc) setSelectedDocId(currentDoc.id);
    } catch (err) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.patch(`/projects/${id}`, editData);
      setProject({ ...project, ...editData });
      setEditing(false);
      toast.success('Project updated');
    } catch (err) {
      toast.error('Failed to update project');
    }
  };

  const handleChecklistToggle = async (itemId, completed) => {
    try {
      await api.patch(`/projects/${id}/checklist/${itemId}`, { completed: !completed });
      setProject({
        ...project,
        checklistItems: project.checklistItems.map(item =>
          item.id === itemId ? { ...item, completed: !completed } : item
        )
      });
    } catch (err) {
      toast.error('Failed to update checklist');
    }
  };

  const handleAddChecklistItem = async (e) => {
    e.preventDefault();
    if (!newChecklistItem.trim()) return;

    try {
      const res = await api.post(`/projects/${id}/checklist`, { label: newChecklistItem });
      setProject({
        ...project,
        checklistItems: [...project.checklistItems, res.data.item]
      });
      setNewChecklistItem('');
      toast.success('Item added');
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  // Google Docs handlers
  const handleDocSelected = async (docData) => {
    try {
      const res = await api.post(`/project-docs/${id}`, {
        url: docData.url,
        title: docData.title,
        role: docData.role || 'DRAFT',
        isCurrent: docData.isCurrent || docs.length === 0
      });
      
      const newDoc = res.data.doc;
      if (newDoc.isCurrent) {
        setDocs([newDoc, ...docs.map(d => ({ ...d, isCurrent: false }))]);
      } else {
        setDocs([newDoc, ...docs]);
      }
      setSelectedDocId(newDoc.id);
      setActiveTab('documents');
    } catch (err) {
      toast.error('Failed to attach document');
    }
  };

  const handleUpdateDoc = async (docId, updates) => {
    try {
      await api.patch(`/project-docs/${id}/${docId}`, updates);
      setDocs(docs.map(d => d.id === docId ? { ...d, ...updates } : d));
      toast.success('Document updated');
    } catch (err) {
      toast.error('Failed to update document');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Remove this document from the project?')) return;
    try {
      await api.delete(`/project-docs/${id}/${docId}`);
      setDocs(docs.filter(d => d.id !== docId));
      if (selectedDocId === docId) {
        setSelectedDocId(docs.find(d => d.id !== docId)?.id || null);
      }
      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  const handleSetCurrentDoc = async (docId) => {
    try {
      await api.patch(`/project-docs/${id}/${docId}`, { isCurrent: true });
      setDocs(docs.map(d => ({ ...d, isCurrent: d.id === docId })));
      toast.success('Current document updated');
    } catch (err) {
      toast.error('Failed to update document');
    }
  };

  const handleExportCalendar = async () => {
    try {
      const response = await api.get(`/ical/download?projectId=${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.title || project.type}-deadlines.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Calendar exported');
    } catch (err) {
      toast.error('Failed to export calendar');
    }
  };

  const handleCopyCitation = async (source, style, type = 'bibliography') => {
    try {
      const res = await api.get(`/sources/${source.id}/citation/${style}`);
      const text = type === 'intext' ? res.data.inText : res.data.bibliography;
      await navigator.clipboard.writeText(text);
      toast.success(`${type === 'intext' ? 'In-text citation' : 'Citation'} copied!`);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Research tab handlers
  const handleFetchSourceMeta = async () => {
    if (!addSourceInput.trim()) return;
    setAddSourceFetching(true);
    setAddSourceMeta(null);
    try {
      const res = await api.post('/sources/fetch-metadata', { input: addSourceInput.trim() });
      setAddSourceMeta({ ...res.data.metadata, canonicalUrl: res.data.canonicalUrl, sourceType: res.data.metadata.sourceType || 'WEBSITE' });
    } catch (err) {
      toast.error('Failed to fetch metadata');
    } finally {
      setAddSourceFetching(false);
    }
  };

  const handleAddSourceToProject = async () => {
    if (!addSourceMeta) return;
    setAddSourceSaving(true);
    try {
      const res = await api.post('/sources', {
        ...addSourceMeta,
        projectId: id
      });
      const newSource = res.data.source;
      setProject(prev => ({
        ...prev,
        projectSources: [...(prev.projectSources || []), { source: { ...newSource, notes: [] }, relevanceScore: null }]
      }));
      setAddSourceOpen(false);
      setAddSourceInput('');
      setAddSourceMeta(null);
      toast.success('Source added to project');
    } catch (err) {
      if (err.response?.data?.existingId) {
        // Source already exists — link it
        try {
          await api.post(`/projects/${id}/sources`, { sourceId: err.response.data.existingId });
          const srcRes = await api.get(`/sources/${err.response.data.existingId}`);
          setProject(prev => ({
            ...prev,
            projectSources: [...(prev.projectSources || []), { source: { ...srcRes.data.source, notes: srcRes.data.source.notes || [] }, relevanceScore: null }]
          }));
          setAddSourceOpen(false);
          setAddSourceInput('');
          setAddSourceMeta(null);
          toast.success('Existing source linked to project');
        } catch {
          toast.error('Failed to link existing source');
        }
      } else {
        toast.error(err.response?.data?.message || 'Failed to add source');
      }
    } finally {
      setAddSourceSaving(false);
    }
  };

  const handleSetRelevance = async (sourceId, score) => {
    try {
      await api.patch(`/projects/${id}/sources/${sourceId}`, { relevanceScore: score });
      setProject(prev => ({
        ...prev,
        projectSources: prev.projectSources.map(ps =>
          ps.source.id === sourceId ? { ...ps, relevanceScore: score } : ps
        )
      }));
    } catch (err) {
      toast.error('Failed to update relevance');
    }
  };

  const handleUnlinkSource = async (sourceId) => {
    if (!window.confirm('Remove this source from the project?')) return;
    try {
      await api.delete(`/projects/${id}/sources/${sourceId}`);
      setProject(prev => ({
        ...prev,
        projectSources: prev.projectSources.filter(ps => ps.source.id !== sourceId)
      }));
      toast.success('Source removed from project');
    } catch (err) {
      toast.error('Failed to remove source');
    }
  };

  const handleCopyBibliography = async (style) => {
    try {
      const res = await api.get(`/projects/${id}/bibliography?style=${style}`);
      await navigator.clipboard.writeText(res.data.bibliography);
      toast.success(`${style.toUpperCase()} bibliography copied!`);
    } catch (err) {
      toast.error('Failed to copy bibliography');
    }
  };

  const handleDownloadBibtex = async () => {
    try {
      const res = await api.get(`/projects/${id}/bibliography?format=bibtex`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bibliography.bib');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('BibTeX downloaded');
    } catch (err) {
      toast.error('Failed to download BibTeX');
    }
  };

  const handleBrowserSearch = async () => {
    if (!browserSearch.trim()) return;
    setBrowserLoading(true);
    setBrowserResults([]);
    try {
      const res = await api.get(`/source-browser/search?q=${encodeURIComponent(browserSearch.trim())}`);
      setBrowserResults(res.data.results || []);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setBrowserLoading(false);
    }
  };

  const handleSaveBrowserResult = async (result) => {
    try {
      const res = await api.post('/sources', {
        title: result.title,
        authors: result.authors || [],
        publisher: result.publisher || '',
        publishedDate: result.publishedDate || '',
        canonicalUrl: result.url || '',
        doi: result.doi || '',
        sourceType: result.sourceType || 'WEBSITE',
        projectId: id
      });
      const newSource = res.data.source;
      setProject(prev => ({
        ...prev,
        projectSources: [...(prev.projectSources || []), { source: { ...newSource, notes: [] }, relevanceScore: null }]
      }));
      toast.success('Source saved to project');
    } catch (err) {
      if (err.response?.data?.existingId) {
        try {
          await api.post(`/projects/${id}/sources`, { sourceId: err.response.data.existingId });
          toast.success('Source linked to project');
        } catch {
          toast.error('Source already in project');
        }
      } else {
        toast.error('Failed to save source');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading project...</div>
      </div>
    );
  }

  if (!project) return null;

  const completedItems = project.checklistItems?.filter(i => i.completed).length || 0;
  const totalItems = project.checklistItems?.length || 0;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const currentDoc = docs.find(d => d.isCurrent);

  return (
    <div className="max-w-6xl mx-auto animate-fade-in" data-testid="project-detail-page">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate('/projects')}
          data-testid="back-to-projects-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Badge variant="outline" className={projectTypeColors[project.type]}>
                {project.type.replace('_', ' ')}
              </Badge>
              {project.subject && (
                <Badge variant="outline" className="bg-slate-50">{project.subject}</Badge>
              )}
              {project.courseLevel && (
                <Badge variant="outline" className="bg-slate-50">{project.courseLevel}</Badge>
              )}
            </div>
            
            {editing ? (
              <Input
                value={editData.title || ''}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                className="text-2xl font-bold mb-2"
                placeholder="Project title"
                data-testid="edit-title-input"
              />
            ) : (
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                {project.title || 'Untitled Project'}
              </h1>
            )}
          </div>

          <div className="flex items-center gap-2">
            {editing ? (
              <Button onClick={handleSave} data-testid="save-project-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-project-btn">
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {currentDoc && (
              <Button
                onClick={() => window.open(currentDoc.url, '_blank')}
                data-testid="open-current-doc-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in Google Docs
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="w-4 h-4" />
            Documents
            {docs.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{docs.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="research">Research</TabsTrigger>
          <TabsTrigger value="gmail" className="gap-2">
            <Mail className="w-4 h-4" />
            Gmail
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-6">
              {/* Research Question */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Research Question</CardTitle>
                </CardHeader>
                <CardContent>
                  {editing ? (
                    <Textarea
                      value={editData.researchQuestion || ''}
                      onChange={(e) => setEditData({ ...editData, researchQuestion: e.target.value })}
                      placeholder="Your research question..."
                      rows={3}
                      data-testid="edit-rq-input"
                    />
                  ) : (
                    <p className="text-slate-700 leading-relaxed">
                      {project.researchQuestion || 'No research question set'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Planning Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  {editing ? (
                    <Textarea
                      value={editData.notes || ''}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      placeholder="Add planning notes..."
                      rows={4}
                      data-testid="edit-notes-input"
                    />
                  ) : (
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {project.notes || 'No notes yet'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Checklist */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                      Checklist
                    </CardTitle>
                    <span className="text-sm text-slate-500">
                      {completedItems} of {totalItems} completed
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
                    <div 
                      className="h-full bg-indigo-500 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {project.checklistItems?.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                          item.completed ? 'bg-green-50' : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={() => handleChecklistToggle(item.id, item.completed)}
                          data-testid={`checklist-item-${item.id}`}
                        />
                        <span className={item.completed ? 'line-through text-slate-400' : 'text-slate-700'}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <form onSubmit={handleAddChecklistItem} className="flex gap-2 mt-4">
                    <Input
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder="Add new item..."
                      className="flex-1"
                      data-testid="new-checklist-input"
                    />
                    <Button type="submit" size="icon" data-testid="add-checklist-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Status</CardTitle>
                </CardHeader>
                <CardContent>
                  {editing ? (
                    <Select 
                      value={editData.status} 
                      onValueChange={(v) => setEditData({ ...editData, status: v })}
                    >
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className="bg-indigo-100 text-indigo-700">
                      {statusOptions.find(o => o.value === project.status)?.label}
                    </Badge>
                  )}
                </CardContent>
              </Card>

              {/* Quick Doc Access */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      Documents
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {currentDoc ? (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-indigo-600" fill="currentColor" />
                        <span className="text-xs text-indigo-600 font-medium">Current Document</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900 mb-2 truncate">
                        {currentDoc.title || 'Untitled'}
                      </p>
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => window.open(currentDoc.url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open in Google Docs
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-2">No documents attached</p>
                  )}
                  
                  <GoogleDocsPicker
                    projectId={id}
                    onDocSelected={handleDocSelected}
                    buttonVariant="outline"
                    buttonSize="sm"
                    className="w-full"
                  />
                  
                  {docs.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full"
                      onClick={() => setActiveTab('documents')}
                    >
                      View all {docs.length} document{docs.length !== 1 ? 's' : ''}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Deadlines */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                      Deadlines
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/deadlines')}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.deadlines?.length > 0 ? (
                    <div className="space-y-2">
                      {project.deadlines.slice(0, 5).map((deadline) => (
                        <div key={deadline.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <span className="text-sm text-slate-700 truncate">{deadline.label}</span>
                          <span className="text-xs text-slate-500">{format(new Date(deadline.dueAt), 'MMM d')}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No deadlines yet</p>
                  )}
                  <Button
                    variant="outline"
                    className="w-full mt-3"
                    onClick={handleExportCalendar}
                    data-testid="export-calendar-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export to Calendar
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900">Project Documents</h2>
                <p className="text-sm text-slate-500">Attach and preview your Google Docs</p>
              </div>
              <GoogleDocsPicker
                projectId={id}
                onDocSelected={handleDocSelected}
                buttonText="Attach Google Doc"
              />
            </div>

            {docs.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">No documents yet</h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Attach your Google Docs to preview them here and keep everything organized.
                  </p>
                  <GoogleDocsPicker
                    projectId={id}
                    onDocSelected={handleDocSelected}
                    buttonText="Select from Google Drive"
                  />
                </CardContent>
              </Card>
            ) : (
              <GoogleDocsPanel
                docs={docs}
                selectedDocId={selectedDocId}
                onSelectDoc={setSelectedDocId}
                onUpdateDoc={handleUpdateDoc}
                onDeleteDoc={handleDeleteDoc}
                onSetCurrent={handleSetCurrentDoc}
              />
            )}
          </div>
        </TabsContent>

        {/* Research Tab */}
        <TabsContent value="research">
          {(() => {
            const sources = project.projectSources || [];
            const sourceCount = sources.length;
            const ibTarget = IB_SOURCE_TARGETS[project.type] || { min: 5, target: 10 };
            const progressPct = Math.min(100, Math.round((sourceCount / ibTarget.target) * 100));
            const progressColor = sourceCount >= ibTarget.target
              ? 'bg-green-500'
              : sourceCount >= ibTarget.min
              ? 'bg-amber-500'
              : 'bg-red-400';

            return (
              <div className="space-y-6">
                {/* Research question context */}
                {project.researchQuestion && (
                  <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                    <p className="text-xs font-medium text-indigo-500 uppercase mb-1">Research Question</p>
                    <p className="text-slate-800 font-medium">{project.researchQuestion}</p>
                  </div>
                )}

                {/* Source count progress */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">
                        Sources: <span className="font-bold text-slate-900">{sourceCount}</span>
                        <span className="text-slate-400 font-normal"> / {ibTarget.target} target</span>
                      </span>
                      <span className="text-xs text-slate-500">Min: {ibTarget.min}</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${progressColor}`}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    {sourceCount < ibTarget.min && (
                      <p className="text-xs text-red-600 mt-1.5">
                        Need {ibTarget.min - sourceCount} more source{ibTarget.min - sourceCount !== 1 ? 's' : ''} to meet the IB minimum.
                      </p>
                    )}
                    {sourceCount >= ibTarget.min && sourceCount < ibTarget.target && (
                      <p className="text-xs text-amber-600 mt-1.5">
                        {ibTarget.target - sourceCount} more to reach the recommended target.
                      </p>
                    )}
                    {sourceCount >= ibTarget.target && (
                      <p className="text-xs text-green-600 mt-1.5">Source target reached.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Toolbar */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button onClick={() => { setAddSourceOpen(true); setAddSourceMeta(null); setAddSourceInput(''); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Source
                  </Button>
                  <Button variant="outline" onClick={() => setFindSourcesOpen(v => !v)}>
                    <Search className="w-4 h-4 mr-2" />
                    Find Sources
                  </Button>
                  <div className="ml-auto">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" disabled={sourceCount === 0}>
                          <Download className="w-4 h-4 mr-2" />
                          Export
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleCopyBibliography('mla')}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy MLA Bibliography
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopyBibliography('apa')}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy APA Bibliography
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopyBibliography('chicago')}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Chicago Bibliography
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleDownloadBibtex}>
                          <Download className="w-4 h-4 mr-2" />
                          Download BibTeX (.bib)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Find Sources Panel */}
                {findSourcesOpen && (
                  <Card className="border-indigo-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Find Sources</CardTitle>
                        <Button variant="ghost" size="icon" onClick={() => setFindSourcesOpen(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2 mb-4">
                        <Input
                          value={browserSearch}
                          onChange={e => setBrowserSearch(e.target.value)}
                          placeholder="Search for sources..."
                          onKeyDown={e => e.key === 'Enter' && handleBrowserSearch()}
                        />
                        <Button onClick={handleBrowserSearch} disabled={browserLoading}>
                          <Search className="w-4 h-4" />
                        </Button>
                      </div>
                      {browserLoading && <p className="text-sm text-slate-500 text-center py-2">Searching...</p>}
                      {browserResults.length > 0 && (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {browserResults.map((result, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 line-clamp-2">{result.title}</p>
                                {result.authors?.length > 0 && (
                                  <p className="text-xs text-slate-500 mt-0.5">{result.authors.join(', ')}</p>
                                )}
                              </div>
                              <Button size="sm" variant="outline" onClick={() => handleSaveBrowserResult(result)}>
                                <Plus className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {!browserLoading && browserResults.length === 0 && browserSearch && (
                        <p className="text-sm text-slate-400 text-center py-2">No results</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Source list */}
                {sources.length > 0 ? (
                  <div className="space-y-3">
                    {sources.map(({ source, relevanceScore }) => {
                      const SourceIcon = sourceTypeIcons[source.sourceType] || BookOpen;
                      const isExpanded = expandedSources[source.id];
                      const notesByType = (source.notes || []).reduce((acc, n) => {
                        (acc[n.noteType] = acc[n.noteType] || []).push(n);
                        return acc;
                      }, {});

                      return (
                        <Card key={source.id} className="overflow-hidden">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <SourceIcon className="w-4 h-4 text-slate-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <Link
                                      to={`/research/${source.id}`}
                                      className="font-medium text-slate-900 hover:text-indigo-600 line-clamp-2 text-sm"
                                    >
                                      {source.title}
                                    </Link>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {source.authors?.length > 0 ? source.authors.join(', ') : 'Unknown author'}
                                      {source.publishedDate && ` • ${source.publishedDate}`}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {/* Quick cite */}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-7 px-2">
                                          <Copy className="w-3.5 h-3.5" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleCopyCitation(source, 'mla')}>MLA</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleCopyCitation(source, 'mla', 'intext')}>MLA In-text</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleCopyCitation(source, 'apa')}>APA</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleCopyCitation(source, 'apa', 'intext')}>APA In-text</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleCopyCitation(source, 'chicago')}>Chicago</DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                    {/* Unlink */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-slate-400 hover:text-red-600"
                                      onClick={() => handleUnlinkSource(source.id)}
                                      title="Remove from project"
                                    >
                                      <Unlink className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>

                                {/* Relevance rating */}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-slate-400">Relevance:</span>
                                  <StarRating
                                    value={relevanceScore || 0}
                                    onChange={(score) => handleSetRelevance(source.id, score)}
                                  />
                                </div>

                                {/* Notes toggle */}
                                {(source.notes?.length > 0) && (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 mt-2 text-xs text-indigo-600 hover:text-indigo-800"
                                    onClick={() => setExpandedSources(prev => ({ ...prev, [source.id]: !prev[source.id] }))}
                                  >
                                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    {source.notes.length} note{source.notes.length !== 1 ? 's' : ''}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Expanded notes */}
                            {isExpanded && source.notes?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 pl-12">
                                {Object.entries(notesByType).map(([type, notes]) => (
                                  <div key={type}>
                                    <p className="text-xs font-medium text-slate-500 mb-1 uppercase">{noteTypeLabels[type] || type}</p>
                                    {notes.map(note => {
                                      const NoteIcon = noteTypeIcons[note.noteType] || MessageSquare;
                                      return (
                                        <div key={note.id} className={`p-2.5 rounded-lg text-xs mb-1 ${noteTypeColors[note.noteType] || 'bg-slate-100 text-slate-700'}`}>
                                          <div className="flex items-center gap-1 mb-1">
                                            <NoteIcon className="w-3 h-3" />
                                            {note.pageRef && (
                                              <span className="text-slate-500 ml-auto">p. {note.pageRef}</span>
                                            )}
                                          </div>
                                          <p className={note.noteType === 'QUOTE' ? 'italic' : ''}>
                                            {note.noteType === 'QUOTE' ? `"${note.content}"` : note.content}
                                          </p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <Card className="text-center py-12">
                    <CardContent>
                      <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">No sources yet</h3>
                      <p className="text-slate-600 mb-6">Add your first source to start building your bibliography</p>
                      <Button onClick={() => setAddSourceOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Source
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* Gmail Tab */}
        <TabsContent value="gmail">
          <div className="space-y-4">
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-1">Gmail</h2>
              <p className="text-sm text-slate-500">
                Filter threads by supervisor email or subject keywords, and compose messages directly.
              </p>
            </div>
            <GmailPanel
              supervisorEmails={project.supervisorEmails || []}
              emailSubjectFilters={project.emailSubjectFilters || []}
              contextTitle={project.title || project.type}
              onUpdateFilters={async ({ supervisorEmails, emailSubjectFilters }) => {
                try {
                  await api.patch(`/projects/${id}`, { supervisorEmails, emailSubjectFilters });
                  setProject((prev) => ({ ...prev, supervisorEmails, emailSubjectFilters }));
                } catch {
                  toast.error('Failed to save email filters');
                }
              }}
            />
          </div>
        </TabsContent>

        {/* Add Source Dialog */}
        <Dialog open={addSourceOpen} onOpenChange={setAddSourceOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Source to Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={addSourceInput}
                  onChange={e => setAddSourceInput(e.target.value)}
                  placeholder="Enter URL, DOI, ISBN, or title..."
                  onKeyDown={e => e.key === 'Enter' && handleFetchSourceMeta()}
                  autoFocus
                />
                <Button onClick={handleFetchSourceMeta} disabled={addSourceFetching || !addSourceInput.trim()}>
                  {addSourceFetching ? 'Fetching...' : 'Fetch'}
                </Button>
              </div>

              {addSourceMeta && (
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 uppercase">Title</label>
                    <Input
                      value={addSourceMeta.title || ''}
                      onChange={e => setAddSourceMeta(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-500 uppercase">Authors</label>
                      <Input
                        value={Array.isArray(addSourceMeta.authors) ? addSourceMeta.authors.join(', ') : (addSourceMeta.authors || '')}
                        onChange={e => setAddSourceMeta(prev => ({ ...prev, authors: e.target.value.split(',').map(a => a.trim()).filter(Boolean) }))}
                        placeholder="Author 1, Author 2"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-500 uppercase">Year</label>
                      <Input
                        value={addSourceMeta.publishedDate || ''}
                        onChange={e => setAddSourceMeta(prev => ({ ...prev, publishedDate: e.target.value }))}
                        placeholder="2024"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-500 uppercase">Publisher</label>
                      <Input
                        value={addSourceMeta.publisher || ''}
                        onChange={e => setAddSourceMeta(prev => ({ ...prev, publisher: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-slate-500 uppercase">Type</label>
                      <Select
                        value={addSourceMeta.sourceType || 'WEBSITE'}
                        onValueChange={v => setAddSourceMeta(prev => ({ ...prev, sourceType: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WEBSITE">Website</SelectItem>
                          <SelectItem value="BOOK">Book</SelectItem>
                          <SelectItem value="JOURNAL">Journal</SelectItem>
                          <SelectItem value="PDF">PDF</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddSourceOpen(false)}>Cancel</Button>
              <Button onClick={handleAddSourceToProject} disabled={!addSourceMeta || addSourceSaving}>
                {addSourceSaving ? 'Adding...' : 'Add to Project'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Tabs>
    </div>
  );
};

export default ProjectDetailPage;
